import React, { useState, useMemo, useEffect, useCallback } from "react";
import { Link, useLocation } from "react-router-dom";
import styled from "styled-components";
import { SidebarMenu, ExtraRoutes } from "../../config/page_route";
import colors from "../../design/colors";
import fonts from "../../design/typography";
import { rootStore } from "../../mobx/store.js";
import { ChevronDown, ChevronRight } from "../icons/chevron";
import { observer } from "mobx-react";
import Close from "../icons/close";
import AdminBrand from "../admin_brand";
import { deviceWidth } from "../../design/defaultTheme";
import { useWindowSize } from "@/hooks/useWindowSize";
import {
  hasPermission,
  getFirstAccessibleRoute,
} from "@/utils/routePermission";
interface SidebarProps {
  isOpen?: boolean;
  onClose?: () => void;
}

const Sidebar = ({ isOpen, onClose }: SidebarProps) => {
  const userType = rootStore.getProfile?.userType ?? "STUDENT";
  const userRole = rootStore.getProfile?.role ?? null;
  const userPermissions = rootStore.getPermissions || [];
  const isCorpMode = rootStore.getCorpMode;

  const updatedMenu = useMemo(() => {
    // 권한이 아직 로드되지 않은 경우 (초기 로딩 중) 로딩 상태 표시
    if (
      userRole === "ADMIN" &&
      (!userPermissions || userPermissions.length === 0)
    ) {
      // 로딩 중일 때는 빈 배열 대신 로딩 메뉴 표시
      return [
        {
          path: "/loading",
          title: "권한 로딩 중...",
          children: [],
        },
      ];
    }

    return SidebarMenu.map((menu) => {
      // 권한 체크
      if (!hasPermission(menu, userRole, userPermissions, userType)) {
        return null;
      }

      if (menu.hasOwnProperty("children")) {
        const updatedChildren = menu.children.filter((child) => {
          return hasPermission(child, userRole, userPermissions, userType);
        });

        // 접근 가능한 서브메뉴가 없으면 부모 메뉴도 숨김
        if (updatedChildren.length === 0) {
          return null;
        }

        return { ...menu, children: updatedChildren };
      }

      return menu;
    }).filter((menu) => menu !== null);
  }, [userType, userRole, userPermissions]);

  const currentPath = useLocation().pathname;

  // 모든 메뉴가 처음에 열려있도록 초기 상태 설정
  const initialMenuState = updatedMenu.map((menu) => {
    if (!menu) return false;
    return true; // 모든 메뉴를 열린 상태로 설정
  });

  const [isMenuOpen, setIsMenuOpen] = useState<boolean[]>(initialMenuState);
  const [hoverdIndex, setHoverdIndex] = useState<number[]>([-1, -1]);

  // updatedMenu가 변경될 때 기존 상태 유지, 새 메뉴만 열린 상태로 초기화
  useEffect(() => {
    setIsMenuOpen((prev) =>
      updatedMenu.map((menu, i) => {
        if (!menu) return false;
        return i < prev.length ? prev[i] : true;
      })
    );
  }, [updatedMenu]);

  const isSelected = (menu: any) => {
    return menu.children.some((child) =>
      child.containPath.some((p) =>
        new RegExp(`^${p.replace(/:[^\s/]+/g, "[^/]+")}$`).test(currentPath)
      )
    );
  };

  const handleMenuClick = () => {
    if (onClose) {
      onClose();
    }
  };

  const { width } = useWindowSize();
  const isMobile = width ? width < deviceWidth.mobile : false;
  const isTablet = width
    ? width >= deviceWidth.mobile && width <= deviceWidth.tablet
    : false;

  return (
    <>
      <Overlay $isOpen={isOpen} onClick={onClose} />
      <Container $isOpen={isOpen}>
        <div style={{ position: "relative" }}>
          {(isMobile || isTablet) && (
            <CloseButton onClick={onClose}>
              <Close width="24px" height="24px" color={colors.gray900} />
            </CloseButton>
          )}
        </div>

        <Link
          to={getFirstAccessibleRoute(userRole, userPermissions, userType)}
          className="navbar-brand"
        >
          <AdminBrand />
        </Link>
        <SidebarMenuWrapper>
          {updatedMenu
            .filter((menu) => menu !== null)
            .map((menu, i) => (
              <React.Fragment key={i}>
                <SidebarMenuMain
                  $isHovered={hoverdIndex[0] === i && hoverdIndex[1] === -1}
                  $isSelected={isSelected(menu)}
                  onMouseEnter={() => setHoverdIndex([i, -1])}
                  onMouseLeave={() => setHoverdIndex([-1, -1])}
                >
                  <Link
                    style={{
                      display: "flex",
                      flexDirection: "row",
                      alignItems: "center",
                      cursor: "pointer",
                      textDecoration: "none",
                      width: "100%",
                    }}
                    to={menu.path}
                    onClick={() => {
                      handleMenuClick();
                      setIsMenuOpen(
                        isMenuOpen.map((_, index) =>
                          index === i ? !isMenuOpen[i] : isMenuOpen[index]
                        )
                      );
                    }}
                  >
                    {isMenuOpen[i] ? (
                      <div style={{ cursor: "pointer", marginRight: 6 }}>
                        <ChevronDown
                          width="20px"
                          height="20px"
                          color={colors.gray200}
                        />
                      </div>
                    ) : (
                      <div style={{ cursor: "pointer", marginRight: 6 }}>
                        <ChevronRight
                          width="20px"
                          height="20px"
                          color={colors.gray200}
                        />
                      </div>
                    )}
                    {isSelected(menu) ? menu.iconActive : menu.iconInactive}
                    <div
                      className="menu-title"
                      style={{
                        ...fonts.body3SemiBold,
                        color: isSelected(menu)
                          ? colors.warning
                          : colors.gray500,
                        marginLeft: 8,
                      }}
                    >
                      {menu.title}
                    </div>
                  </Link>
                </SidebarMenuMain>
                {isMenuOpen[i] && (
                  <div
                    style={{
                      width: "100%",
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "flex-start",
                      justifyContent: "flex-start",
                      gap: 8,
                      marginBottom: 20,
                    }}
                  >
                    {menu.children.map((child, j) => (
                      <SidebarMenuSub
                        key={j}
                        isHovered={hoverdIndex[0] === i && hoverdIndex[1] === j}
                        isSelected={child.containPath.some((p) =>
                          new RegExp(
                            `^${p.replace(/:[^\s/]+/g, "[^/]+")}$`
                          ).test(currentPath)
                        )}
                        onMouseEnter={() => setHoverdIndex([i, j])}
                        onMouseLeave={() => setHoverdIndex([-1, -1])}
                      >
                        <Link
                          className="submenu-title"
                          style={{
                            ...(child.containPath.some((p) =>
                              new RegExp(
                                `^${p.replace(/:[^\s/]+/g, "[^/]+")}$`
                              ).test(currentPath)
                            )
                              ? fonts.body3SemiBold
                              : fonts.body3Medium),
                            color: child.containPath.some((p) =>
                              new RegExp(
                                `^${p.replace(/:[^\s/]+/g, "[^/]+")}$`
                              ).test(currentPath)
                            )
                              ? colors.warning
                              : colors.gray500,
                            marginLeft: 40,
                            textDecoration: "none",
                            width: "100%",
                            height: "100%",
                            display: "flex",
                            alignItems: "center",
                          }}
                          to={child.path}
                          onClick={handleMenuClick}
                        >
                          {child.title}
                        </Link>
                      </SidebarMenuSub>
                    ))}
                  </div>
                )}
              </React.Fragment>
            ))}
        </SidebarMenuWrapper>
      </Container>
    </>
  );
};

export default Sidebar;

const Container = styled.div<{ $isOpen?: boolean }>`
  width: 230px;
  height: 100vh;
  display: flex;
  flex-direction: column;
  background-color: ${colors.white};
  padding: 25px 20px 0 20px;
  border-right: 1px solid ${colors.gray100};
  position: fixed;
  left: 0;
  top: 0;
  z-index: 1000;

  ${({ theme }) => theme.device.mobile} {
    display: ${({ $isOpen }) => ($isOpen ? "flex" : "none")};
    width: 80%;
    box-shadow: 2px 0 8px rgba(0, 0, 0, 0.1);
  }

  ${({ theme }) => theme.device.tablet} {
    display: ${({ $isOpen }) => ($isOpen ? "flex" : "none")};
    width: 200px;
    box-shadow: 2px 0 8px rgba(0, 0, 0, 0.1);
  }
`;

const CloseButton = styled.button`
  position: absolute;
  top: 0px;
  right: -10px;
  background: none;
  border: none;
  padding: 8px;
  cursor: pointer;
  z-index: 1001;

  ${({ theme }) => theme.device.mobile} {
    display: block;
  }

  ${({ theme }) => theme.device.tablet} {
    top: -25px;
    right: -20px;
    display: block;
  }
`;

const Overlay = styled.div<{ $isOpen?: boolean }>`
  display: none;
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.5);
  z-index: 999;

  ${({ theme }) => theme.device.mobile} {
    display: ${({ $isOpen }) => ($isOpen ? "block" : "none")};
  }
`;

const SidebarMenuWrapper = styled.div`
  flex: 1;
  overflow-y: auto;
  margin-top: 40px;
  padding-bottom: 40px;
`;

const SidebarMenuMain = styled.div.withConfig({
  shouldForwardProp: (prop) => prop !== "isHovered" && prop !== "isSelected",
})<{ $isHovered?: boolean; $isSelected?: boolean }>`
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: flex-start;
  width: 100%;
  border-radius: 8px;
  padding: 0 8px;
  height: 40px;
  background-color: ${({ $isHovered, $isSelected }) =>
    $isHovered ? colors.warning50 : colors.white};
  margin-bottom: 8px;
`;

const SidebarMenuSub = styled.div.withConfig({
  shouldForwardProp: (prop) => prop !== "isHovered" && prop !== "isSelected",
})<{ isHovered?: boolean; isSelected?: boolean }>`
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  height: 34px;
  background-color: ${({ isHovered, isSelected }) =>
    isHovered || isSelected ? colors.warning50 : colors.white};
  border-radius: 8px;
`;
